import { Box, Button, Divider, useMantineTheme, Grid } from "@mantine/core";
import { memo, useContext } from "react";
import MultiSelect from "src/components/MultiSelect";
import {
  GetLocationDetailCalendarQuery,
  useClearCalendarFiltersMutation,
  useSaveCalendarFiltersMutation,
} from "src/generated/graphql";
import { IFilter, defaultFilters } from "..";
import { AiOutlineDoubleLeft } from "react-icons/ai";
import { AppContext } from "src/api/AppContext";

type IProps = {
  locDetail: GetLocationDetailCalendarQuery["locationOne"];
  filters: IFilter;
  setFilters: (obj: IFilter) => void;
  setNavbarOpened: (bool: boolean) => void;
  handleModal: () => void;
  isAllowWrite: boolean;
};

function Navbar({
  locDetail,
  filters,
  setFilters,
  setNavbarOpened,
  handleModal,
  isAllowWrite,
}: IProps) {
  const theme = useMantineTheme();
  const bg = theme.colors.secondary[6];
  const { currentLocation, currentEmployee } = useContext(AppContext);

  const [saveFilter, { loading: createLoading }] =
    useSaveCalendarFiltersMutation();

  const [clearFilter, { loading: clearLoading }] =
    useClearCalendarFiltersMutation();

  const clearFilters = () => {
    setFilters(defaultFilters);
    if (currentLocation && currentEmployee) {
      clearFilter({
        variables: {
          locationId: currentLocation.id,
        },
      });
    }
  };

  const saveFilters = () => {
    if (currentLocation && currentEmployee) {
      saveFilter({
        variables: {
          data: {
            services: filters.services.map((e) => ({
              id: e,
            })),
            rooms: filters.rooms.map((e) => ({
              id: e,
            })),
            instructors: filters.instructors.map((e) => ({
              id: e,
            })),
            location: { id: currentLocation.id },
          },
        },
      });
    }
  };

  return (
    <Grid.Col
      lg={2}
      md={3}
      sm={4}
      xs={12}
      sx={{
        padding: "inherit",
        position: "relative",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          height: "100%",
          background: theme.colors.secondary[6],
          borderRight: `1px solid ${theme.colors.secondary[0]}`,
          minHeight: "50vh",
        }}
      >
        <>
          <Box
            style={{
              background: bg,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              borderTop: `1px solid ${theme.colors.secondary[0]}`,
              borderBottom: `1px solid ${theme.colors.secondary[0]}`,
            }}
            p={5}
          >
            <AiOutlineDoubleLeft
              size={16}
              onClick={() => setNavbarOpened(false)}
            />
          </Box>
          <Box m={"xs"}>
            <MultiSelect
              label="Services"
              data={
                locDetail?.locationServices?.map((service) => ({
                  value: service.id,
                  label: service.organizationService.name,
                })) || []
              }
              value={filters.services}
              onChange={(e) => setFilters({ ...filters, services: e })}
            />

            <MultiSelect
              label="Instructor"
              data={
                locDetail?.employees?.map((emp) => ({
                  value: emp.id,
                  label: `${emp.user.firstName} ${emp.user.lastName}`,
                })) || []
              }
              value={filters.instructors}
              onChange={(e: string[]) =>
                setFilters({ ...filters, instructors: e })
              }
            />

            <MultiSelect
              label="Room"
              data={
                locDetail?.rooms?.map((room) => ({
                  value: room.id,
                  label: room.name,
                })) || []
              }
              value={filters.rooms}
              onChange={(e: string[]) => setFilters({ ...filters, rooms: e })}
            />
            <Button
              onClick={saveFilters}
              loading={createLoading}
              mt={"xs"}
              mx={"xs"}
              radius="xl"
              sx={{
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
            >
              Save Filters
            </Button>
            <Button
              onClick={clearFilters}
              loading={clearLoading}
              mt={7}
              radius="xl"
              sx={{
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
            >
              Clear Filters
            </Button>
            <Divider mt={"xs"} color={theme.colors.secondary[0]} />
          </Box>
          {isAllowWrite && (
            <Box
              sx={{
                position: "absolute",
                bottom: 10,
                width: "100%",
                margin: "auto",
              }}
            >
              <Button
                radius="xl"
                onClick={() => handleModal()}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
              >
                Create Meeting
              </Button>
            </Box>
          )}
        </>
      </Box>
    </Grid.Col>
  );
}

export default memo(Navbar);
